import type { AxiosError } from 'axios';

/**
 * Server-side error codes we know about from the resource-review
 * spec. These are string tags returned in the response body
 * (typically under `error` / `code`) — separate from HTTP status.
 */
export type ReviewErrorCode =
  | 'STALE_RECORD'
  | 'VALIDATION_ERROR'
  | 'INVALID_CHANGELOG'
  | 'PERMISSION_DENIED'
  | 'NOT_FOUND'
  | 'UNKNOWN';

export interface ReviewError {
  /** Best guess at the machine code — falls back to `'UNKNOWN'`. */
  code: ReviewErrorCode;
  /** HTTP status code (0 when the request never left the client). */
  status: number;
  /** User-facing message — safe to show in a toast or alert. */
  message: string;
  /** Field-level errors if the server surfaced any (VALIDATION_ERROR). */
  fieldErrors?: Record<string, string>;
  /** Raw axios error for callers that need to log the full detail. */
  cause?: unknown;
}

interface ServerErrorBody {
  error?: string;
  code?: string;
  message?: string;
  detail?: string;
  errors?: Record<string, string>;
  field_errors?: Record<string, string>;
  [key: string]: unknown;
}

/**
 * Normalise an axios error thrown by any `/console/review/*` call
 * into a `ReviewError`. Extracts the machine code from either a
 * `code` or `error` field in the response body (backend has been
 * seen using both conventions across the rest of the mis-api), maps
 * common patterns onto stable constants, and produces a message
 * that's safe to render.
 *
 * The messages are deliberately terse and avoid leaking server
 * internals — anything long or opaque is replaced with a friendly
 * fallback keyed off the code. Callers can grab `cause` to log full
 * detail elsewhere.
 */
export function toReviewError(err: unknown): ReviewError {
  const axiosErr = err as AxiosError<ServerErrorBody>;
  const status = axiosErr?.response?.status ?? 0;
  const body = axiosErr?.response?.data;
  const rawCode = (body?.code || body?.error || '').toString().toUpperCase();

  const code = mapCode(rawCode, status);
  const message = friendlyMessage(code, body, axiosErr);
  const fieldErrors = body?.field_errors ?? body?.errors;

  return { code, status, message, fieldErrors, cause: err };
}

function mapCode(rawCode: string, status: number): ReviewErrorCode {
  if (rawCode === 'STALE_RECORD') return 'STALE_RECORD';
  if (rawCode === 'VALIDATION_ERROR') return 'VALIDATION_ERROR';
  if (rawCode === 'INVALID_CHANGELOG') return 'INVALID_CHANGELOG';
  if (rawCode === 'PERMISSION_DENIED' || status === 403) return 'PERMISSION_DENIED';
  if (rawCode === 'NOT_FOUND' || status === 404) return 'NOT_FOUND';
  if (status === 409) return 'STALE_RECORD';
  if (status === 422) return 'VALIDATION_ERROR';
  return 'UNKNOWN';
}

function friendlyMessage(
  code: ReviewErrorCode,
  body: ServerErrorBody | undefined,
  err: AxiosError | undefined,
): string {
  const serverMsg = (body?.message || body?.detail || '').toString().trim();

  switch (code) {
    case 'STALE_RECORD':
      return 'This record was updated by someone else while you were editing. Reload to pick up the latest values, then re-apply your changes.';
    case 'VALIDATION_ERROR':
      return serverMsg || 'Some of the values you entered are not valid. Please review the highlighted fields and try again.';
    case 'INVALID_CHANGELOG':
      return 'No change history is available to restore from.';
    case 'PERMISSION_DENIED':
      return 'You do not have permission to perform this action.';
    case 'NOT_FOUND':
      return 'The record you are looking for is no longer available.';
    case 'UNKNOWN':
    default: {
      if (serverMsg) return serverMsg;
      if (err?.code === 'ERR_NETWORK') return 'Network error — please check your connection and try again.';
      return 'Something went wrong. Please try again in a moment.';
    }
  }
}

/** True when the error indicates the row was modified out from under us. */
export function isStaleRecord(err: unknown): boolean {
  return toReviewError(err).code === 'STALE_RECORD';
}
